import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CircleX, CircuitBoard } from "lucide-react";
import { fromJS } from "immutable";
import DialogLayout from "../../dialog/DialogLayout";
import { getBaseUrl } from "../../api/api";
import { deleteFileLogActivity, uploadFileLogActivity } from "../../api/ProjectApi";
import LayoutAction from "../../actions/LayoutAction";
import ImageView from "./ImageView";
import CanvaDesign from "./CanvaDesign";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import AIContentAction from "../../actions/AIContentAction";
import ImageUpload from "./ImageUpload";

interface ImageUploadDialogProps {
  projectId: string;
  itemId: string;
  open: boolean;
  onClose: () => void;
  onSave: (mediaId: string, mediaType: string, isApplyAll: boolean) => void;
}

const ImageUploadDialog = ({
  projectId,
  itemId,
  open,
  onClose,
  onSave,
}: ImageUploadDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fileImages = useSelector((state: any) => state.aiContent.get("fileImages")) || fromJS([]);

  const [fileNameImage, setFileNameImage] = useState<string | null>(null);
  const [fileImage, setFileImage] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<string>("user");
  const [isApplyAll, setIsApplyAll] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState("");
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const userFileImages = fileImages.filter(
    (item) => item.getIn(["event", "data", "visibility"]) === "Private"
  );
  const teamSharedFileImages = fileImages.filter(
    (item) => item.getIn(["event", "data", "visibility"]) === "Shared"
  );

  useEffect(() => {
    if (userFileImages.size > 0) {
      const fileImage = userFileImages.first();
      setSelectedImageId(fileImage.getIn(["event", "data", "fileName"]) || "");
    }
  }, [userFileImages]);

  const handleSave = async () => {
    // If upload image
    if (fileNameImage && fileImage) {
      await uploadFileLogActivity(itemId, fileImage, "Shared");
      dispatch(AIContentAction.getFileImages(projectId));
      onSave(fileNameImage, "HOST", isApplyAll);
      dispatch(
        LayoutAction.setNotification({
          title: t("Image"),
          type: "success",
          message: t("Uploaded image"),
        })
      );
      // If select from list image
    } else {
      if (selectedImageId) {
        onSave(selectedImageId, "HOST", isApplyAll);
      } else {
        dispatch(
          LayoutAction.setNotification({
            title: t("Image"),
            type: "error",
            message: t("Please upload an image or select an image."),
          })
        );
      }
    }
  };

  const handleDelete = (id) => {
    setShowConfirm({
      show: true,
      title: t("Delete image"),
      description: t("Are you sure you want to delete this image?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: id,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      deleteFileLogActivity(showConfirm.data)
        .then(() => {
          setSelectedImageId("");
          dispatch(AIContentAction.getFileImages(projectId));
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete image"),
              type: "success",
              message: t("Delete image successfully!"),
            })
          );
        })
        .catch((error) => {
          if (error?.status === 409) {
            dispatch(
              LayoutAction.setNotification({
                title: t("Delete image"),
                type: "warning",
                message: t(
                  "The image is currently in use. Please remove it from all posts before deleting."
                ),
              })
            );
          } else {
            dispatch(
              LayoutAction.setNotification({
                title: t("Delete image"),
                type: "error",
                message: t("Failed to delete image."),
              })
            );
          }
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const renderImages = () => {
    if (activeTab === "user") {
      if (userFileImages.size === 0) {
        return <div className="h-80 pt-10 text-sm text-gray-600 text-center">{t("No image")}</div>;
      }
      return (
        <div className="w-full h-80 overflow-y-auto">
          <div className="grid grid-cols-5 gap-4 p-4">
            {userFileImages.map((item) => (
              <div
                key={item.get("id")}
                className={`relative group w-25 h-25 aspect-square overflow-hidden rounded-xl cursor-pointer ${selectedImageId === item.getIn(["event", "data", "fileName"])
                  ? "border-2 border-blue-500 shadow-md shadow-blue-500"
                  : "border border-gray-200"
                  }`}
                onClick={() => {
                  setSelectedImageId(item.getIn(["event", "data", "fileName"]));
                }}
              >
                <ImageView
                  fileName={item.getIn(["event", "data", "fileName"])}
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-1 right-1">
                  <div
                    className="p-0.5 bg-red-500 rounded-md cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(item.get("id"))}
                  >
                    <CircleX className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (activeTab === "team") {
      if (teamSharedFileImages.size === 0) {
        return <div className="h-80 pt-10 text-sm text-gray-600 text-center">{t("No image")}</div>;
      }
      return (
        <div className="w-full h-80 overflow-y-auto">
          <div className="grid grid-cols-5 gap-4 p-4">
            {teamSharedFileImages.map((item) => (
              <div
                key={item.get("id")}
                className={`relative group w-25 h-25 aspect-square overflow-hidden rounded-xl cursor-pointer ${selectedImageId === item.getIn(["event", "data", "fileName"])
                  ? "border-2 border-blue-500 shadow-md shadow-blue-500"
                  : "border border-gray-200"
                  }`}
                onClick={() => {
                  setSelectedImageId(item.getIn(["event", "data", "fileName"]));
                }}
              >
                <ImageView
                  fileName={item.getIn(["event", "data", "fileName"])}
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-1 right-1">
                  <div
                    className="p-0.5 bg-red-500 rounded-md cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(item.get("id"))}
                  >
                    <CircleX className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    } else if (activeTab === "canva") {
      return (
        <div className="w-full h-80">
          <CanvaDesign
            projectId={projectId}
            onExportImage={async (filename, file) => {
              await uploadFileLogActivity(itemId, file, "Shared");
              dispatch(AIContentAction.getFileImages(projectId));
              setActiveTab("team");
              setSelectedImageId(filename);
            }}
            navigateToUrl={(url) => {
              navigate(url);
            }}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <DialogLayout show={open}>
      <div className="flex flex-col items-center justify-center w-full min-w-2xl mx-auto p-0 sm:p-6">
        <h2 className="text-xl font-bold mb-1 text-center">{t("Upload and resize image")}</h2>
        <div className="text-base text-gray-500 mb-4 text-center">{t("Use image up to 3MB")}</div>
        <div className="max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col items-center justify-center">
            <ImageUpload
              selectedImageId={selectedImageId}
              setSelectedImageId={setSelectedImageId}
              onChange={(fileName, file) => {
                if (fileName && file) {
                  setFileNameImage(fileName);
                  setFileImage(file);
                } else {
                  setFileNameImage(null);
                  setFileImage(null);
                  let selectedImageIdData = "";
                  if (activeTab === "user") {
                    if (userFileImages.size > 0) {
                      const fileImage = userFileImages.first();
                      selectedImageIdData = fileImage.getIn(["event", "data", "fileName"]) || "";
                    }
                  } else if (activeTab === "team") {
                    if (teamSharedFileImages.size > 0) {
                      const fileImage = teamSharedFileImages.first();
                      selectedImageIdData = fileImage.getIn(["event", "data", "fileName"]) || "";
                    }
                  }
                  setSelectedImageId(selectedImageIdData);
                }
              }}
            />
          </div>
          {fileImages.size > 0 ? (
            <>
              <div className="w-full flex justify-center gap-3 relative">
                <button
                  onClick={() => {
                    setActiveTab("user");
                    if (userFileImages.size > 0) {
                      const fileImage = userFileImages.first();
                      setSelectedImageId(fileImage.getIn(["event", "data", "fileName"]) || "");
                    } else {
                      setSelectedImageId("");
                    }
                  }}
                  className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 ${activeTab === "user"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-800"
                    }`}
                >
                  <CircuitBoard
                    className={`w-4 h-4 ${activeTab === "user" ? "text-blue-600" : "text-gray-800"}`}
                  />
                  {t("User Media")}
                </button>
                <button
                  onClick={() => {
                    setActiveTab("team");
                    if (teamSharedFileImages.size > 0) {
                      const fileImage = teamSharedFileImages.first();
                      setSelectedImageId(fileImage.getIn(["event", "data", "fileName"]) || "");
                    } else {
                      setSelectedImageId("");
                    }
                  }}
                  className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 ${activeTab === "team"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-800"
                    }`}
                >
                  <CircuitBoard
                    className={`w-4 h-4 ${activeTab === "team" ? "text-blue-600" : "text-gray-800"}`}
                  />
                  {t("Team Shared Media")}
                </button>
                <button
                  onClick={() => {
                    setActiveTab("canva");
                    setSelectedImageId("");
                  }}
                  className={`flex items-center justify-center gap-2 px-6 py-2 text-sm font-medium z-1 ${activeTab === "canva"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-800"
                    }`}
                >
                  <img
                    src={`${getBaseUrl()}/static/images/canva.png`}
                    className="w-4 h-4 object-contain"
                    alt="Canva logo"
                  />
                  {t("Canva")}
                </button>
                <div className="w-full absolute bottom-0 border border-gray-200"></div>
              </div>
              {renderImages()}
            </>
          ) : null}
        </div>
        <div className="w-full flex flex-row justify-between mt-4 gap-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="applyAllToPosts"
              disabled={selectedImageId === ""}
              checked={isApplyAll}
              onChange={(e) => setIsApplyAll(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="applyAllToPosts" className="text-sm font-semibold text-gray-700">
              {t("Apply this image to all posts")}
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-200 text-gray-800 text-sm font-semibold"
              onClick={onClose}
            >
              {t("Cancel")}
            </button>
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold"
              onClick={handleSave}
            >
              {t("Confirm")}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </DialogLayout>
  );
};

export default ImageUploadDialog;
